import React, { useEffect, useState } from "react";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import MenuIcon from "@material-ui/icons/Menu";
import CancelIcon from "@material-ui/icons/Cancel";
import ArrowRightIcon from "@material-ui/icons/ArrowRight";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import styles from "./DraggableList.module.css";
import PuiTooltip from "../../../components/elements/PuiTooltip ";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        menuButton: {
            marginRight: theme.spacing(2),
        },
    })
);

interface DraggableListProps {
    // リストを区別ため
    type: string;
    // 対象リスト
    targetList: DraggableItem[];
    // クリックコールバック関数
    onClick?: any;
    // リスト更新用コールバック関数
    onUpdate?: any;
    // クリック対象
    currentId?: string;
    // リストの高さ(px)
    listHeight?: number;
    // 表示する項目数
    showListNum?: number;
}

interface DraggableItem {
    id: string;
    title: string;
}

const DraggableList = React.forwardRef<HTMLInputElement, DraggableListProps>(function DraggableList(
    props,
    ref
) {
    const { listHeight = 300 } = props;

    const classes = useStyles();
    const [draggable, setDraggable] = useState<DraggableItem[]>();

    useEffect(() => {
        setDraggable(props.targetList);
    }, [props.targetList]);

    // 項目削除
    const handleClick = (id: string, index: number) => {
        const element = document.getElementById(`${props.type}-${id}`);
        if (element) element.style.display = "none";

        // リスト更新（項目削除）
        const order = Array.from(draggable || []);
        order.splice(index, 1);
        setDraggable(order);
        if (props.onUpdate) props.onUpdate(props.type, order, Number(id));
    };

    // 並び替え
    const onDragEnd = (result: any) => {
        // 項目がリスト以外に移動した場合
        if (!result.destination) return;

        // リストの順番を変更
        const reorder = Array.from(draggable || []);
        const [removed] = reorder.splice(result.source.index, 1);
        reorder.splice(result.destination.index, 0, removed);
        // 新しい順番をセット
        setDraggable(reorder);

        // リスト順変更あるか
        if (result.source.index !== result.destination.index) {
            if (props.onUpdate) props.onUpdate(props.type, reorder);
        }
    };

    // リスト項目情報取得
    const selectInfo = (targetId: string) => {
        if (props.onClick) props.onClick(targetId);
    };

    return (
        <>
            <div className={styles.root}>
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="droppable">
                        {(provided) => (
                            <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                style={{ height: String(listHeight) + "px" }}
                                className={styles.scroll}
                            >
                                {draggable &&
                                    draggable.map((target, index) => (
                                        <Draggable
                                            key={target.id}
                                            draggableId={String(target.id)}
                                            index={index}
                                        >
                                            {(provided) => (
                                                <AppBar
                                                    key={`${props.type}-${target.id}`}
                                                    id={`${props.type}-${target.id}`}
                                                    position="static"
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    ref={provided.innerRef}
                                                >
                                                    <Toolbar
                                                        variant="dense"
                                                        style={{
                                                            background:
                                                                String(props.currentId) ===
                                                                String(target.id)
                                                                    ? "var(--studio-btn-bg)"
                                                                    : "#292E36",
                                                            marginTop: index !== 0 ? "-1px" : "",
                                                        }}
                                                        className={styles.toolbar}
                                                    >
                                                        <IconButton
                                                            edge="start"
                                                            className={classes.menuButton}
                                                            aria-label="menu"
                                                            disabled={true}
                                                        >
                                                            <MenuIcon
                                                                style={{
                                                                    color:
                                                                        String(props.currentId) ===
                                                                        String(target.id)
                                                                            ? "#292E36"
                                                                            : "#555",
                                                                }}
                                                                className={styles.menuIcon}
                                                            />
                                                        </IconButton>
                                                        <Typography
                                                            variant="h6"
                                                            onClick={() =>
                                                                selectInfo(`${target.id}`)
                                                            }
                                                            style={{
                                                                color: "white",
                                                            }}
                                                            className={styles.title}
                                                            data-tip={target.title}
                                                            data-for="title"
                                                        >
                                                            {target.title}
                                                        </Typography>
                                                        <PuiTooltip id="title" />
                                                        <IconButton
                                                            edge="start"
                                                            aria-label="menu"
                                                            onClick={() =>
                                                                handleClick(target.id, index)
                                                            }
                                                        >
                                                            <CancelIcon
                                                                style={{
                                                                    display:
                                                                        String(props.currentId) ===
                                                                        String(target.id)
                                                                            ? "none"
                                                                            : "",
                                                                    color: "var(--gray-38)",
                                                                }}
                                                                className={styles.cancelIcon}
                                                            />
                                                        </IconButton>
                                                        {String(props.currentId) ===
                                                            String(target.id) && (
                                                            <ArrowRightIcon
                                                                className={styles.arrowRightIcon}
                                                            />
                                                        )}
                                                    </Toolbar>
                                                </AppBar>
                                            )}
                                        </Draggable>
                                    ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>
        </>
    );
});

export default DraggableList;
